import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Business,
  ExpandMore,
  Search,
  Public,
  Check,
} from '@mui/icons-material';
import { useOrganizations } from '../../hooks/useFleet';
import { useOrganizationContext } from '../../contexts/OrganizationContext';

const OrganizationSelector: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedOrgId, setSelectedOrgId, isGlobalPage } = useOrganizationContext();

  // Fetch organizations
  const { data: orgsData, isLoading } = useOrganizations({ size: 100 });
  const organizations = orgsData?.data || [];

  // Find selected org name
  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  // Filter organizations by search
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!isGlobalPage) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelectOrg = (orgId: string | null) => {
    setSelectedOrgId(orgId);
    handleClose();
  };

  // Global page view - show disabled "Global View" indicator
  if (isGlobalPage) {
    return (
      <Tooltip title="This page shows global resources shared across all organizations" arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderRadius: '10px',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            cursor: 'default',
          }}
        >
          <Public sx={{ fontSize: 20, color: '#6366f1' }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Global View
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        sx={{
          px: 2,
          py: 0.75,
          borderRadius: '10px',
          bgcolor: selectedOrgId ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: selectedOrgId ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent',
          color: selectedOrgId ? '#a5b4fc' : 'text.primary',
          textTransform: 'none',
          '&:hover': {
            bgcolor: selectedOrgId ? 'rgba(99, 102, 241, 0.25)' : 'rgba(255, 255, 255, 0.1)',
          },
        }}
        endIcon={<ExpandMore />}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              maxWidth: { xs: 100, sm: 150, md: 200 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedOrg ? selectedOrg.name : 'All Organizations'}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            bgcolor: 'rgba(30, 30, 30, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {/* Search */}
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* All Organizations option */}
        <MenuItem
          onClick={() => handleSelectOrg(null)}
          selected={selectedOrgId === null}
          sx={{
            mx: 1,
            borderRadius: '8px',
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Public sx={{ fontSize: 20, color: '#6366f1' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                All Organizations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                View data from all customers
              </Typography>
            </Box>
            {selectedOrgId === null && <Check sx={{ color: '#6366f1', fontSize: 20 }} />}
          </Box>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Organization list */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredOrgs.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No organizations found' : 'No organizations available'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            {filteredOrgs.map((org) => (
              <MenuItem
                key={org.id}
                onClick={() => handleSelectOrg(org.id)}
                selected={selectedOrgId === org.id}
                sx={{
                  mx: 1,
                  borderRadius: '8px',
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Business sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {org.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {org.site_count || 0} sites
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.device_count || 0} devices
                      </Typography>
                    </Box>
                  </Box>
                  {selectedOrgId === org.id && <Check sx={{ color: '#6366f1', fontSize: 20 }} />}
                </Box>
              </MenuItem>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default OrganizationSelector;
